import { PlusOutlined } from '@ant-design/icons'
import { Button, Select, Space, Spin, Table, Tabs } from 'antd'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useClusters } from '@/hooks/useClusters'
import { useUserGroups } from '@/hooks/useUserGroups'
import { useVmConfigurations } from '@/hooks/useVmConfigurations'

import type { PriceResourceType } from './AddPriceModal'
import { AddPriceModal } from './AddPriceModal'
import {
  clusterColumn,
  formatVmConfig,
  pricePerHourColumn,
  pricePerMonthColumn,
} from './priceColumns'
import type { PriceGroup } from './useGroupPrices'
import { useGroupPrices } from './useGroupPrices'

const EMPTY = { emptyText: 'No prices for this group' }
const SCROLL = { x: 700 }

function PriceGroupTable({
  groups,
  groupName,
  resourceType,
}: {
  groups: PriceGroup[]
  groupName: string
  resourceType: PriceResourceType
}) {
  const navigate = useNavigate()

  return (
    <Table<PriceGroup>
      dataSource={groups}
      rowKey="key"
      pagination={false}
      size="small"
      scroll={SCROLL}
      locale={EMPTY}
      onRow={(record) => ({
        style: { cursor: 'pointer' },
        onClick: () =>
          navigate(
            `/user-groups/${groupName}/prices/${resourceType}/${encodeURIComponent(record.key)}`,
          ),
      })}
      columns={[
        clusterColumn((r) => r.clusterId),
        {
          title: 'Resource',
          dataIndex: 'resourceLabel',
          width: 220,
          sorter: (a, b) => a.resourceLabel.localeCompare(b.resourceLabel),
        },
        pricePerHourColumn((r) => r.currentPrice?.pricePerHourPerQty ?? '0'),
        pricePerMonthColumn((r) => r.currentPrice?.pricePerHourPerQty ?? '0'),
      ]}
    />
  )
}

export function GroupPricesTab({ groupName }: { groupName: string }) {
  const [activeResource, setActiveResource] = useState<PriceResourceType>('vm')
  const [addModal, setAddModal] = useState<PriceResourceType | null>(null)
  const [configFilter, setConfigFilter] = useState<string | undefined>()
  const [clusterId, setClusterId] = useState<string | undefined>()
  const { vmGroups, storageGroups, ipGroups, loading, refetchAll } =
    useGroupPrices(groupName, clusterId)
  const clusters = useClusters()
  const { configurations } = useVmConfigurations()
  const userGroups = useUserGroups()

  const filteredVmGroups = useMemo(
    () =>
      configFilter
        ? vmGroups.filter((g) => g.key.endsWith(`:${configFilter}`))
        : vmGroups,
    [vmGroups, configFilter],
  )

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Select
          placeholder="All clusters"
          allowClear
          style={{ width: 250 }}
          value={clusterId}
          onChange={setClusterId}
        >
          {clusters.map((c) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
        {activeResource === 'vm' && (
          <Select
            placeholder="All configurations"
            allowClear
            style={{ width: 300 }}
            value={configFilter}
            onChange={setConfigFilter}
          >
            {configurations.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {formatVmConfig(c)}
              </Select.Option>
            ))}
          </Select>
        )}
      </Space>

      <Tabs
        activeKey={activeResource}
        onChange={(k) => setActiveResource(k as PriceResourceType)}
        items={[
          {
            key: 'vm',
            label: `VM (${filteredVmGroups.length})`,
            children: (
              <PriceGroupTable
                groups={filteredVmGroups}
                groupName={groupName}
                resourceType="vm"
              />
            ),
          },
          {
            key: 'storage',
            label: `Storage (${storageGroups.length})`,
            children: (
              <PriceGroupTable
                groups={storageGroups}
                groupName={groupName}
                resourceType="storage"
              />
            ),
          },
          {
            key: 'public-ip',
            label: `Public IP (${ipGroups.length})`,
            children: (
              <PriceGroupTable
                groups={ipGroups}
                groupName={groupName}
                resourceType="public-ip"
              />
            ),
          },
        ]}
        tabBarExtraContent={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setAddModal(activeResource)}
          >
            Add Price
          </Button>
        }
      />

      {addModal && (
        <AddPriceModal
          open
          groups={userGroups}
          prefill={{
            groupName,
            resourceType: addModal,
          }}
          onClose={() => setAddModal(null)}
          onSuccess={() => {
            setAddModal(null)
            refetchAll()
          }}
        />
      )}
    </Space>
  )
}
